/*
 * km2-cart-smart.js — the cart "Add a little something" smart-picks row.
 *
 * Ranks candidates against the CURRENT cart (fetched from /cart.js) and feeds them to the
 * shared KM2Rail engine (owner decision 2026-07-02) — the SAME carousel as the homepage
 * rails: auto-scroll + seamless loop + pause-on-card-hover + drag + lazy load-as-you-scroll.
 * Cards are cream .ebc-card via the shared KM2ProductCard builder (quickAdd: true).
 * Scoring tiers (top slot first; cheapest-first within a tier — cheap adds are the
 * enticer for the item-count free-shipping goal):
 *   100  EBC dupe of a cart scent  — assets/km2-inspired-index.json (tools/build-inspired-index.mjs)
 *    60  same brand as a cart item — /collections/{vendor-handle}/products.json
 *    40  gift set matching cart gender — gifts-sets-womens / gifts-sets-mens
 *    20  gender+type match from trending-now (filler; always available as fallback)
 * The CURATED ranked list fills the rail first; once it drains, LAZY batches page
 * gender-matched trending via KM2Brands.createProductPager (Storefront GraphQL — immune to
 * the products.json 429 rate limit), up to `max` (schema max_cards) total, then the rail
 * loops. Every rendered card — curated or lazy — is stock-confirmed via
 * /products/{handle}.js (capped) because listings and the dev proxy's availability flags
 * go stale (bug_log#17), and because quick-add needs the NUMERIC variant id the GraphQL
 * pager does not return (its queries carry no variant id — checked 2026-07-02).
 *
 * Cart-page integration: quick-add here must refresh the cart items + footer too, not just
 * the header bubble — km2-product-card.js dispatches 'km2:cart:added' and honors
 * window.KM2QuickAddSections; this file sets that up and swaps the returned section HTML.
 * A cart change (quick-add / qty edit) re-ranks by destroying + rebuilding the rail
 * against fresh /cart.js state (queued-never-dropped, same discipline as before).
 */
(function () {
  if (window.KM2CartSmart) return; // idempotent

  // The ONE tag→gender rule (plan 20261003-165759 PD11), shared with genderFromTags in
  // tools/build-inspired-index.mjs (same case table: tests/unit/cart-smart-gender.test.ts). EBC
  // products carry ebc-men / ebc-women after the relabel; unisex EBC (ebc-unisex, or both) → null,
  // like a designer `unisex` product. Designer results unchanged: women wins over men.
  function genderOf(tags) {
    var t = tags || [];
    var has = function (x) { return t.indexOf(x) !== -1; };
    if (has('ebc-unisex') || (has('ebc-men') && has('ebc-women'))) return null;
    if (has('women') || has('ebc-women')) return 'women';
    if (has('men') || has('ebc-men')) return 'men';
    return null;
  }
  // Exposed BEFORE the host check so a page (or test) without a picks host still reaches it.
  window.KM2CartSmart = { genderOf: genderOf };

  var host = document.querySelector('[data-km2-picks]');
  if (!host) return;

  var cfg = {
    max: parseInt(host.dataset.max, 10) || 4,
    minSubtotal: parseFloat(host.dataset.minSubtotal) || 0, // store currency; 0 = off (settings.km2_free_ship_min_subtotal)
    indexUrl: host.dataset.indexUrl,
    moneyFormat: host.dataset.moneyFormat || '${{amount}}',
    titleLocked: host.dataset.titleLocked || 'Treat yourself',
    subLocked: host.dataset.subLocked || 'Trending on KM2',
  };
  var section = host.closest('.km2-picks');
  var titleEl = section && section.querySelector('.km2-picks__title');
  var subEl = section && section.querySelector('.km2-picks__sub');

  // Generic fragrance-listing words — never evidence that two scents are related ('pour'
  // matched Versace Dylan Blue POUR Femme to a men's 'Versace POUR Homme' dupe).
  var STOPWORDS = ['for', 'women', 'men', 'and', 'the', 'eau', 'de', 'parfum', 'toilette', 'edp', 'edt',
    'spray', 'by', 'oz', 'ml', 'pour', 'homme', 'femme', 'set', 'gift', 'mini', 'refillable', 'intense'];
  function tokens(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
      .filter(function (t) { return t.length > 2 && STOPWORDS.indexOf(t) === -1; });
  }
  function overlap(a, b) {
    var setB = {};
    b.forEach(function (t) { setB[t] = 1; });
    return a.filter(function (t) { return setB[t]; }).length;
  }
  function handleize(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function getJSON(url) {
    return fetch(url).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  // ---- candidate collectors (each returns [{handle, why, whyClass, score}]) ------------
  function itemGender(it) {
    var hay = (it.product_title + ' ' + (it.url || '')).toLowerCase();
    if (/women|for-women/.test(hay)) return 'women';
    if (/\bmen\b|for-men/.test(hay)) return 'men';
    return null;
  }
  function dupeCandidates(index, cartItems, inCart) {
    var out = [];
    (index && index.entries ? index.entries : []).forEach(function (e) {
      if (inCart[e.handle]) return;
      var eTok = tokens(e.inspiredBy);
      var best = 0;
      cartItems.forEach(function (it) {
        // Gender gate: a women's cart scent never surfaces a men's dupe (and vice versa).
        var g = itemGender(it);
        if (e.gender && g && e.gender !== g) return;
        var hit = overlap(eTok, tokens(it.vendor + ' ' + it.product_title));
        if (hit > best) best = hit;
      });
      // >=2 shared meaningful tokens = the dupe's target scent is in the cart
      // (vendor alone can collide across a house's whole line).
      if (best >= 2) {
        // The reason never names the designer (house-brand rule, TONY.md #3): inspiredBy drives the
        // MATCH only; the card's title tooltip says just that it is the EBC alternative.
        out.push({ handle: e.handle, why: 'EBC alternative', whyClass: 'km2-pick-why--dupe', score: 100 + best });
      }
    });
    return out;
  }
  function brandCandidates(cartItems, inCart) {
    var vendors = [];
    cartItems.forEach(function (it) {
      if (it.vendor && !/^ebc/i.test(it.vendor) && vendors.indexOf(it.vendor) === -1) vendors.push(it.vendor);
    });
    return Promise.all(vendors.slice(0, 2).map(function (v) {
      return getJSON('/collections/' + handleize(v) + '/products.json?limit=12').then(function (d) {
        return (d && d.products ? d.products : [])
          .filter(function (p) { return !inCart[p.handle]; })
          .slice(0, 6)
          .map(function (p) { return { handle: p.handle, why: v, whyClass: '', score: 60 }; });
      });
    })).then(function (lists) { return [].concat.apply([], lists); });
  }
  function giftSetCandidates(genders, inCart) {
    var cols = [];
    if (genders.women) cols.push('gifts-sets-womens');
    if (genders.men) cols.push('gifts-sets-mens');
    return Promise.all(cols.map(function (c) {
      return getJSON('/collections/' + c + '/products.json?limit=12').then(function (d) {
        return (d && d.products ? d.products : [])
          .filter(function (p) { return !inCart[p.handle]; })
          .slice(0, 6)
          .map(function (p) { return { handle: p.handle, why: 'Gift set', whyClass: '', score: 40 }; });
      });
    })).then(function (lists) { return [].concat.apply([], lists); });
  }
  function fillerCandidates(genders, inCart) {
    return getJSON('/collections/trending-now/products.json?limit=30').then(function (d) {
      return (d && d.products ? d.products : [])
        .filter(function (p) { return !inCart[p.handle]; })
        .map(function (p) {
          var tags = p.tags || [];
          var g = genderOf(tags);
          var match = !g || genders[g] || (!genders.women && !genders.men);
          return { handle: p.handle, why: g === 'women' ? 'For her' : g === 'men' ? 'For him' : 'Trending', whyClass: '', score: match ? 20 : 5 };
        });
    });
  }

  // ---- stock confirm + rail feed --------------------------------------------------------
  // /products/{handle}.js shape -> the card-builder shape (REST prices are cents).
  // REST product -> the ONE shared card contract (plan 20260901-050844 T6). This was one of six
  // divergent copies of the same mapping; it now delegates so this rail cannot drift from the grid
  // and the tiles again. scale 100 because Ajax/REST prices are in CENTS. The adapter also carries
  // the variant `title` these payloads already had and this mapper used to throw away — that title
  // is what a size picker renders, and it is the point of the plan.
  function normalizeRest(p) {
    if (!p) return null;
    var M = window.KM2ProductModel;
    if (!M) {
      // Fail loudly: km2-product-model.js is included globally from layout/theme.liquid, so its
      // absence is a load-order bug, not a data condition. Returning null silently would empty
      // the rail with no explanation.
      if (window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'cart-smart-picks');
      return null;
    }
    return M.productFromRestProduct(p, { scale: 100 });
  }


  var rail = null;

  function buildRail(candidates, card, genders, inCart) {
    // Dedup by handle (keep highest score), sort score desc — the CURATED queue the rail
    // drains first. Price ordering happens per confirmed batch (price known only after
    // /products/{handle}.js).
    var byHandle = {};
    candidates.forEach(function (c) {
      if (!byHandle[c.handle] || byHandle[c.handle].score < c.score) byHandle[c.handle] = c;
    });
    var ranked = Object.keys(byHandle).map(function (h) { return byHandle[h]; })
      .sort(function (a, b) { return b.score - a.score; });

    var dead = false;            // set when a re-rank destroys this rail instance
    var shown = {};              // handles already rendered by THIS rail instance
    var i = 0, probes = 0, MAX_PROBES = 40; // whole-rail budget (bug_log#17 429 discipline)
    var total = 0;
    var pager = null, pagerDone = false;

    // Confirm ranked candidates one by one (stock truth + numeric variant id for quick-add).
    function confirm(c) {
      probes++;
      return getJSON('/products/' + c.handle + '.js').then(function (p) {
        var ok = p && (p.variants || []).some(function (x) { return x.available; });
        if (!ok || shown[c.handle] || inCart[c.handle]) return null;
        shown[c.handle] = 1;
        return { meta: c, product: normalizeRest(p), price: p.price || 0 };
      });
    }

    function curatedNext(want) {
      var out = [];
      function step() {
        if (dead || out.length >= want || i >= ranked.length || probes >= MAX_PROBES) return Promise.resolve(out);
        return confirm(ranked[i++]).then(function (x) { if (x) out.push(x); return step(); });
      }
      return step();
    }

    // Lazy source: gender-matched trending via the Storefront GraphQL pager (backend
    // in-stock-filtered, rate-limit-immune). It DISCOVERS handles; each rendered card is
    // still REST-confirmed (numeric variant id + availability truth). Pager built lazily
    // on first use — same deferred-script race guard as ebc-spotlight.liquid.
    function lazyNext(want) {
      if (dead || pagerDone) return Promise.resolve([]);
      if (!pager) {
        var KB = window.KM2Deps
          ? window.KM2Deps.need('KM2Brands', 'createProductPager', 'cart-smart-picks')
          : ((window.KM2Brands && window.KM2Brands.createProductPager) ? window.KM2Brands : null);
        pager = KB ? KB.createProductPager({ collectionHandle: 'trending-now' }) : null;
        // Do NOT latch pagerDone here: an absent dependency is a load-order fault, not an
        // exhausted feed. Latching it made a transient miss permanent for the whole page.
        if (!pager) return Promise.resolve([]);
      }
      var out = [];
      var pages = 0;
      function pull() {
        if (dead || out.length >= want || pages >= 4 || probes >= MAX_PROBES) return Promise.resolve(out);
        pages++;
        return pager.nextPage().then(function (page) {
          var fresh = (page.nodes || []).filter(function (n) {
            if (!n || !n.handle || inCart[n.handle] || shown[n.handle]) return false;
            var tags = n.tags || [];
            var g = genderOf(tags);
            return !g || genders[g] || (!genders.women && !genders.men);
          });
          function drain() {
            if (dead || out.length >= want || !fresh.length || probes >= MAX_PROBES) return Promise.resolve();
            var n = fresh.shift();
            var g = genderOf(n.tags);
            var why = g === 'women' ? 'For her' : g === 'men' ? 'For him' : 'Trending';
            return confirm({ handle: n.handle, why: why, score: 20 }).then(function (x) {
              if (x) out.push(x);
              return drain();
            });
          }
          return drain().then(function () {
            if (!page.hasMore) { pagerDone = true; return out; }
            return pull();
          });
        }).catch(function () { pagerDone = true; return out; });
      }
      return pull();
    }

    // KM2Rail fetchBatch: curated first, lazy trending tops up; [] once the pool cap
    // (cfg.max) or every source is dry -> the rail engages its seamless loop.
    var firstDone = false;
    function fetchBatch(limit) {
      if (dead || total >= cfg.max) return Promise.resolve([]);
      var want = Math.min(limit, cfg.max - total);
      return curatedNext(want).then(function (items) {
        if (items.length >= want) return items;
        return lazyNext(want - items.length).then(function (more) { return items.concat(more); });
      }).then(function (items) {
        // Within equal score, cheapest first — the low price is the enticement.
        items.sort(function (a, b) { return (b.meta.score - a.meta.score) || (a.price - b.price); });
        total += items.length;
        firstDone = true;
        return items;
      });
    }

    function renderItem(x) {
      var el = card.build(x.product);
      // Owner call (2026-07-02): NO badge pill on the card — it fought the hover
      // quick-add button visually. The match reason still drives RANKING and is
      // kept as title/aria metadata for a future treatment (tooltip etc.).
      if (x.meta.why) el.setAttribute('title', x.meta.why);
      return el;
    }

    host.innerHTML = '';
    var instance = window.KM2Rail.create({
      host: host,
      cardSelector: '.ebc-card', // quick-add cards are <div> roots; image/button hover must pause
      rowCount: 1,
      rowClass: 'ebc-scroller',
      trackClass: 'ebc-track',
      batchSize: 8,
      trigger: 4,
      speed: 60, // matches the homepage EBC/audience product rails
      fetchBatch: fetchBatch,
      renderItem: renderItem,
      onFirstBatch: function (rows) {
        var el = rows[0] && rows[0].el;
        if (el) { el.tabIndex = 0; el.setAttribute('aria-label', 'Suggested products'); }
        section && (section.hidden = false);
      }
    });
    return {
      destroy: function () {
        dead = true;
        instance.destroy();
      },
      // Resolves after the FIRST batch settles (rail boots async; empty -> hide section).
      settled: function (cb) {
        var t0 = Date.now();
        (function poll() {
          if (dead) return cb();
          if (firstDone) {
            if (total === 0) section && (section.hidden = true);
            return cb();
          }
          if (Date.now() - t0 > 30000) return cb(); // safety: never wedge the re-rank queue
          setTimeout(poll, 200);
        })();
      }
    };
  }

  // ---- main -----------------------------------------------------------------------------
  // A re-rank requested mid-run must QUEUE, not drop — dropping left the row stale after a
  // second quick-add landed while the first add's re-rank was still fetching (and the
  // unlocked heading swap never ran). One queued slot is enough: runs are idempotent
  // against the latest /cart.js state.
  var refreshing = false, rerunQueued = false;
  function run() {
    if (refreshing) { rerunQueued = true; return; }
    refreshing = true;
    Promise.all([getJSON('/cart.js'), getJSON(cfg.indexUrl)]).then(function (rs) {
      var cart = rs[0], index = rs[1];
      if (!cart || !cart.items || !cart.items.length) {
        section && (section.hidden = true);
        if (rail) { rail.destroy(); rail = null; }
        done();
        return;
      }
      var inCart = {};
      var genders = { women: false, men: false };
      cart.items.forEach(function (it) {
        inCart[it.handle] = true;
        var hay = (it.product_title + ' ' + it.url).toLowerCase();
        if (/women|for-women/.test(hay)) genders.women = true;
        else if (/\bmen\b|for-men/.test(hay)) genders.men = true;
      });
      // Same test as snippets/km2-free-ship-bar.liquid: cart.total_price is in cents.
      var unlocked = cfg.minSubtotal > 0 && cart.total_price >= Math.round(cfg.minSubtotal * 100);
      if (titleEl && unlocked) titleEl.textContent = cfg.titleLocked;
      if (subEl && unlocked) subEl.textContent = cfg.subLocked;

      var card = window.KM2ProductCard.create({ moneyFormat: cfg.moneyFormat, quickAdd: true });
      var dupes = dupeCandidates(index, cart.items, inCart);
      return Promise.all([
        brandCandidates(cart.items, inCart),
        giftSetCandidates(genders, inCart),
        fillerCandidates(genders, inCart)
      ]).then(function (rest) {
        var all = dupes.concat(rest[0], rest[1], rest[2]);
        // Re-rank = destroy + rebuild against the fresh cart (rail owns the DOM rows).
        if (rail) { rail.destroy(); rail = null; }
        rail = buildRail(all, card, genders, inCart);
        rail.settled(done);
      });
    }).catch(function (e) {
      done();
      if (window.console) console.warn('[km2 cart-smart]', e);
    });
    function done() {
      refreshing = false;
      if (rerunQueued) { rerunQueued = false; run(); }
    }
  }

  // Cart-page section refresh after OUR quick-add: swap cart items + footer + re-rank.
  var itemsEl = document.getElementById('main-cart-items');
  var footerEl = document.getElementById('main-cart-footer');
  var extra = [];
  if (itemsEl && itemsEl.dataset.id) extra.push(itemsEl.dataset.id);
  if (footerEl && footerEl.dataset.id) extra.push(footerEl.dataset.id);
  window.KM2QuickAddSections = extra;
  document.addEventListener('km2:cart:added', function (e) {
    var sections = (e.detail && e.detail.sections) || {};
    [itemsEl, footerEl].forEach(function (el) {
      if (!el || !el.dataset.id || !sections[el.dataset.id]) return;
      var doc = new DOMParser().parseFromString(sections[el.dataset.id], 'text/html');
      var fresh = doc.querySelector('.js-contents');
      var cur = el.querySelector('.js-contents');
      if (fresh && cur) cur.innerHTML = fresh.innerHTML;
      // An add can turn the "empty cart" template into a populated one only via reload;
      // handle the common non-empty path inline and fall back to reload when leaving empty.
      if (el === itemsEl && el.classList.contains('is-empty')) window.location.reload();
    });
    run(); // re-rank: the added product must drop out of the row
  });

  // Re-rank when the shopper edits quantities through the cart's own controls.
  var debounceTimer;
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest && e.target.closest('cart-items, .cart-item')) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(run, 1200); // after cart.js's own AJAX settles
    }
  });

  // Deferred-boot: wait for the shared card builder + rail engine.
  function boot(tries) {
    if (window.KM2ProductCard && window.KM2Rail) { run(); return; }
    if (tries > 0) setTimeout(function () { boot(tries - 1); }, 50);
  }
  // KM2Deps.ready: 'interactive' means parsed but DEFERRED SCRIPTS NOT YET RUN, so the old
  // `else` branch fired before the shared globals existed. Only 'complete' runs immediately.
  var __km2boot = function () { boot(40); };
  (window.KM2Deps ? window.KM2Deps.ready(__km2boot)
    : (document.readyState === 'complete' ? __km2boot()
       : document.addEventListener('DOMContentLoaded', __km2boot, { once: true })));
})();
